import React, { useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import dataService from '../services/dataService';
import localDbService from '../services/localDbService';

const OrderDetailsModal = ({ order, isOpen, onClose }) => {
  if (!isOpen || !order) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    const fetchDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await localDbService.getOrderDetails(order['Ord.Ser']);
        if (active) setDetails(data);
      } catch (e) {
        if (active) setError('Falha ao carregar detalhes');
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchDetails();
    return () => { active = false; };
  }, [order]);

  const getStatusBadge = (status) => {
    const normalize = (s) => {
      if (s === null || s === undefined) return 'Não informado';
      const str = typeof s === 'string' ? s : String(s);
      const map = {
        '0': 'Aguardando diagnóstico',
        '1': 'Orçada/aguardando aprovação',
        '2': 'Aguardando início da execução',
        '3': 'Em execução',
        '4': 'Parada/aguardando material',
        '5': 'Concluída',
        '6': 'Entregue',
        '7': 'Orçamento não aprovado',
        '9': 'Cancelada'
      };
      return map[str] || str;
    };
    const value = normalize(status);
    if (!value) return <span className="badge-warning">Não informado</span>;
    if (value.includes('Concluída')) return <span className="badge-success">{value}</span>;
    if (value.includes('Aguardando')) return <span className="badge-warning">{value}</span>;
    if (value.includes('Parada/aguardando material')) return <span className="badge-warning">{value}</span>;
    if (value.includes('Entregue')) return <span className="badge-success">{value}</span>;
    if (value.includes('Orçamento não aprovado')) return <span className="badge-danger">{value}</span>;
    if (value.includes('Cancelada')) return <span className="badge-danger">{value}</span>;
    if (value.includes('Pendente')) return <span className="badge-danger">{value}</span>;
    return <span className="badge-info">{value}</span>;
  };

  

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white">
            Detalhes da Ordem de Serviço #{dataService.safeValue(order['Ord.Ser'])}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-1"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Informações Básicas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Data de Inclusão
                </label>
                <p className="text-white bg-gray-700 p-2 rounded">
                  {dataService.formatDate(order['Data Inclusao'])}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Técnico Responsável
                </label>
                <p className="text-white bg-gray-700 p-2 rounded">
                  {dataService.safeValue(order['Técnico'])}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Status da OS
                </label>
                <div className="bg-gray-700 p-2 rounded">
                  {getStatusBadge(details?.header?.posicao || order['Posição'])}
                </div>
              </div>
              
              
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Valor do Serviço
                </label>
                <p className="text-white bg-gray-700 p-2 rounded font-mono">
                  {dataService.formatCurrency(details?.totals?.servicos ?? order['Vlr.cobrado ser'])}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Valor do Material
                </label>
                <p className="text-white bg-gray-700 p-2 rounded font-mono">
                  {dataService.formatCurrency(details?.totals?.materiais ?? order['Vlr.cobrado mat'])}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Valor Total
                </label>
                <p className="text-white bg-primary p-2 rounded font-mono font-bold">
                  {dataService.formatCurrency(
                    details?.totals?.total ??
                    ((parseFloat(order['Vlr.cobrado ser']) || 0) + (parseFloat(order['Vlr.cobrado mat']) || 0))
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Itens com serviços e materiais */}
          <div className="border-t border-gray-700 pt-4">
            <h3 className="text-lg font-semibold text-white mb-3">Itens</h3>
            {loading && <div className="text-gray-400">Carregando…</div>}
            {error && <div className="text-red-400">{error}</div>}
            {!loading && !error && (details?.items?.length > 0 ? (
              <div className="space-y-6">
                {details.items.map((it, idx) => (
                  <div key={`${it.item_number}-${idx}`} className="bg-gray-750 rounded p-3 border border-gray-700">
                    <div className="overflow-x-auto mb-3">
                      <table className="table-dark">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Produto</th>
                            <th>Status</th>
                            <th className="text-right">Valor</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>{it.item_number}</td>
                            <td className="max-w-xs truncate" title={it.product_description || it.product_code}>
                              {it.product_description || it.product_code}
                            </td>
                            <td>{getStatusBadge(it.posicao || it.pos_code)}</td>
                            <td className="text-right font-mono">{dataService.formatCurrency(it.value)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    
                    {Array.isArray(it.services) && it.services.length > 0 && (
                      <div className="mb-3">
                        <h4 className="text-white font-medium mb-2">Serviços do item</h4>
                        <div className="overflow-x-auto">
                          <table className="table-dark">
                        <thead>
                              <tr>
                                <th>#</th>
                                <th>Serviço</th>
                                <th>Qtd</th>
                                <th>Unitário</th>
                                <th>Status</th>
                            <th>Faturamento</th>
                                <th className="text-right">Valor</th>
                              </tr>
                            </thead>
                            <tbody>
                              {it.services.map((s, i2) => (
                                <tr key={`${s.service_number}-${i2}`}>
                                  <td>{s.service_number}</td>
                                  <td className="max-w-xs truncate" title={s.product_description || s.service_id}>
                                    {s.product_description || s.service_id}
                                  </td>
                                  <td>{s.charged_quantity}</td>
                                  <td>{dataService.formatCurrency(s.unit_value)}</td>
                                  <td>{getStatusBadge(s.posicao || s.pos_code)}</td>
                              <td>
                                {s.billing_status === 'Faturado' ? (
                                  <span className="badge-success">Faturado</span>
                                ) : s.billing_status === 'Cancelado' ? (
                                  <span className="badge-danger">Cancelado</span>
                                ) : (
                                  <span className="badge-warning">Não faturado</span>
                                )}
                              </td>
                                  <td className="text-right font-mono">{dataService.formatCurrency(s.value)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {Array.isArray(it.materials) && it.materials.length > 0 && (
                      <div>
                        <h4 className="text-white font-medium mb-2">Materiais do item</h4>
                        <div className="overflow-x-auto">
                          <table className="table-dark">
                        <thead>
                              <tr>
                                <th>#</th>
                                <th>Produto</th>
                                <th>Qtd</th>
                                <th>Unitário</th>
                                <th>Status</th>
                            <th>Faturamento</th>
                                <th className="text-right">Valor</th>
                              </tr>
                            </thead>
                            <tbody>
                              {it.materials.map((m, i3) => (
                                <tr key={`${m.material_number}-${i3}`}>
                                  <td>{m.material_number}</td>
                                  <td className="max-w-xs truncate" title={m.product_description || m.product_id}>
                                    {m.product_description || m.product_id}
                                  </td>
                                  <td>{m.charged_quantity}</td>
                                  <td>{dataService.formatCurrency(m.unit_value)}</td>
                                  <td>{getStatusBadge(m.posicao || m.pos_code)}</td>
                              <td>
                                {m.billing_status === 'Faturado' ? (
                                  <span className="badge-success">Faturado</span>
                                ) : m.billing_status === 'Cancelado' ? (
                                  <span className="badge-danger">Cancelado</span>
                                ) : (
                                  <span className="badge-warning">Não faturado</span>
                                )}
                              </td>
                                  <td className="text-right font-mono">{dataService.formatCurrency(m.value)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-400">Sem itens</div>
            ))}
          </div>

          {/* Descrição e Problema */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Descrição do Serviço
              </label>
              <p className="text-white bg-gray-700 p-3 rounded min-h-[60px]">
                {dataService.safeValue(order['Descrição'])}
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Problema Relatado
              </label>
              <p className="text-white bg-gray-700 p-3 rounded min-h-[60px]">
                {dataService.safeValue(order['Problema'])}
              </p>
            </div>
          </div>

          {/* Informações do Cliente */}
          <div className="border-t border-gray-700 pt-4">
            <h3 className="text-lg font-semibold text-white mb-4">Informações do Cliente</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Cliente
                </label>
                <p className="text-white bg-gray-700 p-2 rounded">
                  {dataService.safeValue(order['Cliente'])}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Telefone
                </label>
                <p className="text-white bg-gray-700 p-2 rounded">
                  {dataService.safeValue(order['Telefone'])}
                </p>
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Endereço
                </label>
                <p className="text-white bg-gray-700 p-2 rounded">
                  {dataService.safeValue(order['Endereço'])}
                </p>
              </div>
            </div>
          </div>

          {/* Observações */}
          {order['Observações'] && (
            <div className="border-t border-gray-700 pt-4">
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Observações
              </label>
              <p className="text-white bg-gray-700 p-3 rounded min-h-[60px]">
                {dataService.safeValue(order['Observações'])}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-700">
          <button
            onClick={onClose}
            className="btn-secondary px-6 py-2"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsModal;
